import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { FolderOpen, Pencil, Trash2, Upload, Loader2, Image, GripVertical, Plus } from 'lucide-react';

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', image: '', order: 0, active: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', image: '', order: 0 });
  const [createUploading, setCreateUploading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getAdminCategories();
      setCategories(data.categories || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const openEdit = (cat) => {
    setEditForm({
      name: cat.name,
      image: cat.image || '',
      order: cat.order || 0,
      active: cat.active !== false,
    });
    setEditDialog(cat);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCategory(editDialog.slug, editForm);
      toast.success('Categoria actualizada');
      setEditDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Eliminar la categoria "${cat.name}"? Solo se permite si no tiene anuncios.`)) return;
    try {
      await api.deleteCategory(cat.slug);
      toast.success(`Categoria "${cat.name}" eliminada`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadFile(file);
      setEditForm(prev => ({ ...prev, image: `/api/files/${result.id}` }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const toSlug = (text) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleCreateImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCreateUploading(true);
    try {
      const result = await api.uploadFile(file);
      setCreateForm(prev => ({ ...prev, image: `/api/files/${result.id}` }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error('Error al subir imagen');
    } finally {
      setCreateUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    const slug = createForm.slug.trim() || toSlug(createForm.name);
    setSaving(true);
    try {
      await api.createCategory({
        name: createForm.name.trim(),
        slug,
        image_url: createForm.image || null,
        order: createForm.order || (categories.length + 1),
      });
      toast.success(`Categoria "${createForm.name}" creada`);
      setCreateDialog(false);
      setCreateForm({ name: '', slug: '', image: '', order: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 mb-1">Gestion de Categorias</h1>
          <p className="text-xs text-zinc-600">{categories.length} categorias configuradas</p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-500 text-white"
          onClick={() => { setCreateForm({ name: '', slug: '', image: '', order: categories.length + 1 }); setCreateDialog(true); }}
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva categoria
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(cat => (
            <div key={cat.slug}
              className={`rounded-xl border overflow-hidden transition-all ${cat.active === false ? 'border-red-500/20 opacity-60' : 'border-white/[0.06]'} bg-[#0A0A0D]`}
              data-testid={`cat-card-${cat.slug}`}>
              {/* Image */}
              <div className="aspect-[16/9] relative bg-zinc-900">
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-zinc-700" />
                  </div>
                )}
                {cat.active === false && (
                  <div className="absolute top-2 right-2 text-[10px] bg-red-500/80 text-white px-2 py-0.5 rounded-full">Desactivada</div>
                )}
                <div className="absolute top-2 left-2 text-[10px] bg-black/60 text-zinc-300 px-2 py-0.5 rounded-full">
                  #{cat.order}
                </div>
              </div>
              {/* Content */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-zinc-200">{cat.name}</h3>
                  <span className="text-[10px] text-zinc-600">{cat.slug}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">{cat.listing_count || 0} anuncios</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/5"
                      onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.slug}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDelete(cat)} data-testid={`delete-cat-${cat.slug}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Editar "{editDialog?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Nombre</Label>
              <Input value={editForm.name} onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="edit-cat-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Imagen</Label>
              {editForm.image && (
                <div className="mb-2 rounded-lg overflow-hidden aspect-video">
                  <img src={editForm.image} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2">
                <Input value={editForm.image} onChange={e => setEditForm(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="URL de imagen" className="bg-white/5 border-white/10 text-zinc-100 text-xs flex-1" data-testid="edit-cat-image" />
                <label className="flex items-center gap-1 px-3 rounded-md border border-white/10 cursor-pointer hover:bg-white/5 transition-colors">
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                  {uploading ? <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" /> : <Upload className="w-4 h-4 text-zinc-400" />}
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Orden</Label>
              <Input type="number" value={editForm.order} onChange={e => setEditForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="bg-white/5 border-white/10 text-zinc-100 w-24" data-testid="edit-cat-order" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Activa</Label>
              <Switch checked={editForm.active} onCheckedChange={v => setEditForm(prev => ({ ...prev, active: v }))} data-testid="edit-cat-active" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditDialog(null)} className="border-white/10 text-zinc-400">Cancelar</Button>
              <Button className="bg-purple-600 hover:bg-purple-500 text-white" onClick={handleSave} disabled={saving} data-testid="save-cat">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={() => setCreateDialog(false)}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Nueva categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Nombre</Label>
              <Input
                value={createForm.name}
                onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value, slug: prev.slug || '' }))}
                placeholder="Ej: Mamiferos, Roedores, Alimento Vivo"
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="create-cat-name"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Slug (se genera automatico si lo dejas vacio)</Label>
              <Input
                value={createForm.slug}
                onChange={e => setCreateForm(prev => ({ ...prev, slug: e.target.value }))}
                placeholder={createForm.name ? toSlug(createForm.name) : 'ej: mamiferos'}
                className="bg-white/5 border-white/10 text-zinc-100 text-xs" data-testid="create-cat-slug"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Imagen</Label>
              {createForm.image && (
                <div className="mb-2 rounded-lg overflow-hidden aspect-video">
                  <img src={createForm.image} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2">
                <Input value={createForm.image} onChange={e => setCreateForm(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="URL de imagen" className="bg-white/5 border-white/10 text-zinc-100 text-xs flex-1" data-testid="create-cat-image" />
                <label className="flex items-center gap-1 px-3 rounded-md border border-white/10 cursor-pointer hover:bg-white/5 transition-colors">
                  <input type="file" accept="image/*" className="hidden" onChange={handleCreateImageUpload} disabled={createUploading} />
                  {createUploading ? <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" /> : <Upload className="w-4 h-4 text-zinc-400" />}
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1">Orden</Label>
              <Input type="number" value={createForm.order} onChange={e => setCreateForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="bg-white/5 border-white/10 text-zinc-100 w-24" data-testid="create-cat-order" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setCreateDialog(false)} className="border-white/10 text-zinc-400">Cancelar</Button>
              <Button className="bg-purple-600 hover:bg-purple-500 text-white" onClick={handleCreate} disabled={saving} data-testid="create-cat-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Crear categoria
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
